import React, { useState, useEffect, useContext, forwardRef } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Button, Dialog, DialogContent, DialogActions, DialogTitle, Slide } from '@material-ui/core';
import { Link as RouterLink } from "react-router-dom";
import Title from './Title';
import ShowState from './showState';
import moment from 'moment';
import { serverContext, appContext, fetchFromServer, postToServer } from '../App';
import { StServerVersion } from 'stserver-frontend-comp';
import { green, red } from '@material-ui/core/colors';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import { useServerGet } from '../customEffects';


const Transition = forwardRef((props, ref) => {
    return <Slide direction="up" {...props} ref={ref} />;
})

const ReverseProxyStatus = props => {
    return props.useReverseProxy ?
        <Tooltip title="Running behind reverse proxy">
            <CheckCircleOutlineOutlinedIcon style={{ color: green[500] }} />
        </Tooltip>
        :
        <Tooltip title="Running without reverse proxy">
            <ErrorOutlineIcon style={{ color: red[500] }} />
        </Tooltip>
}


const BackupsStatus = ({ backups, isLoading, error }) => {
    if (isLoading) {
        return <CircularProgress size={16} />;
    }

    if (error) {
        return <Typography variant="body2" color="error" gutterBottom>Backups unavailable</Typography>;
    }

    return backups && backups > 0 ?
        <Tooltip title="Backups found">
            <Typography variant="body2" gutterBottom>{backups}</Typography>
        </Tooltip>
        :
        <Typography variant="body2" gutterBottom>No backups</Typography>
}



export default function ServerStatus(props) {

    const { server } = useContext(serverContext);
    const { dispatchApp } = useContext(appContext);
    const [uptime, setUptime] = useState('-');
    const [systemUptime, setSystemUptime] = useState('-');
    const [diskSpace, setDiskSpace] = useState('-');
    const [isRestartDlgOpen, setIsRestartDlgOpen] = useState(false);
    const { data: backups, errorRet, isLoading } = useServerGet('backups/count', server);

    useEffect(() => {
        const interval = setInterval(() => {
            if (server.serverStartTime != null) {
                const d = moment.duration(moment().diff(server.serverStartTime));
                if (d.asHours() > 24)
                    setUptime(d.humanize());
                else
                    setUptime(moment.utc(moment().diff(server.serverStartTime)).format('HH:mm:ss'));
            }
            else
                setUptime('-');

        }, 1000);
        return () => clearInterval(interval);
    }, [server.serverStartTime]);


    useEffect(() => {
        let isMounted = true;
        let timeoutId;

        const pollSystemInfo = async () => {
            try {
                const info = await fetchFromServer('systeminfo', server);
                const d = moment.duration(info.data.systemUptime * 1000);
                if (isMounted) {
                    setSystemUptime(d.humanize());
                    const disk = info.data.diskSpace || info.data.diskspace || info.data.disk;
                    const freeBytes = disk?.freeBytes ?? disk?.free ?? info.data.diskFreeBytes ?? info.data.diskFree;
                    const totalBytes = disk?.totalBytes ?? disk?.total ?? info.data.diskTotalBytes ?? info.data.diskTotal;
                    if (typeof freeBytes === 'number' && typeof totalBytes === 'number' && totalBytes > 0) {
                        const toGb = bytes => (bytes / (1024 * 1024 * 1024));
                        const freeGb = toGb(freeBytes);
                        const totalGb = toGb(totalBytes);
                        setDiskSpace(`${freeGb.toFixed(1)} / ${totalGb.toFixed(1)} GB`);
                    } else {
                        setDiskSpace('-');
                    }
                }
            } catch (error) {
                if (isMounted) {
                    setSystemUptime('-');
                    setDiskSpace('-');
                }
            } finally {
                if (isMounted) {
                    timeoutId = setTimeout(pollSystemInfo, 5000);
                }
            }
        };

        pollSystemInfo();

        return () => {
            isMounted = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [server]);


    const handleOnUpdateClick = async ver => {
        console.log(`update to ${ver}`);
        if (props.onVersionUpdate)
            props.onVersionUpdate(ver);
    }

    function handleReset() {
        setIsRestartDlgOpen(true);
    }

    const handleClose = () => {
        setIsRestartDlgOpen(false);
    };

    const handleRestart = async () => {
        try {
            await postToServer('shutdown?restart', {}, server);
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Restarting the server...`, severity: 'warning' } });
        } catch (error) {
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Failed to restart the server`, severity: 'error' } });
        } finally {
            setIsRestartDlgOpen(false);
        }
    };

    return (
        <React.Fragment>

            <Grid item container spacing={1}>
                <Grid item xs={6}>
                    <Title>Server</Title>
                </Grid>
                <Grid item xs={6}>
                    <ShowState state={server.state} />
                </Grid>
            </Grid>
            <Grid item container spacing={0} alignItems="center" >
                <Grid item xs={5} >
                    <Typography variant="body2" gutterBottom>Name</Typography>
                </Grid>
                <Grid item xs={7} zeroMinWidth>
                    <Typography variant="body2" noWrap style={{ maxWidth: '100%' }}>{server.serverName}</Typography>
                </Grid>
                <Grid item xs={5}>
                    <Typography variant="body2" gutterBottom>Version</Typography>
                </Grid>
                <Grid item xs={7} zeroMinWidth style={{ minWidth: 0 }}>
                    <div style={{ overflow: 'hidden' }}>
                        <StServerVersion currentVersion={server.serverVersion} versionInfoUrl={'https://api.github.com/repos/impleotv/stserver-release/releases/latest'} showUpdateButtonIfNeeded={true} onUpdateClick={handleOnUpdateClick} />
                    </div>
                </Grid>
                <Grid item xs={5}>
                    <Typography variant="body2" gutterBottom>Server Uptime</Typography>
                </Grid>
                <Grid item xs={5} zeroMinWidth>
                    <Typography variant="body2" noWrap style={{ maxWidth: '100%' }}>{uptime}</Typography>
                </Grid>
                <Grid item xs={2}>
                    <Tooltip title='Restart server'>
                        <IconButton aria-label="reset" onClick={handleReset} style={{ height: '24px', width: '24px' }}>
                            <PowerSettingsNewIcon size="small" color="secondary" />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item xs={5}>
                    <Typography variant="body2" gutterBottom>System Uptime</Typography>
                </Grid>
                <Grid item xs={7} zeroMinWidth>
                    <Typography variant="body2" noWrap style={{ maxWidth: '100%' }}>{systemUptime}</Typography>
                </Grid>
                <Grid item xs={5}>
                    <Typography variant="body2" gutterBottom>Disk space</Typography>
                </Grid>
                <Grid item xs={7} zeroMinWidth>
                    <Tooltip title="Free / Total in GB">
                        <Typography variant="body2" noWrap style={{ maxWidth: '100%' }}>{diskSpace}</Typography>
                    </Tooltip>
                </Grid>
                <Grid item xs={5}>
                    <Link component={RouterLink} to="/backups" color="primary">
                        Backups
                    </Link>
                </Grid>
                <Grid item xs={7} zeroMinWidth>
                    <BackupsStatus backups={backups} isLoading={isLoading} error={errorRet} />
                </Grid>
                <Grid item xs={5}>
                    <Typography variant="body2" gutterBottom>Reverse proxy</Typography>
                </Grid>
                <Grid item xs={7} zeroMinWidth>
                    <ReverseProxyStatus useReverseProxy={server.useReverseProxy} />
                </Grid>
            </Grid>

            <Dialog
                open={isRestartDlgOpen}
                TransitionComponent={Transition}
            >
                <DialogTitle id="restart-dialog-slide-title">

                </DialogTitle>
                <DialogContent>
                    <p>Are you sure you want to restart?</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRestart} color="primary">
                        Restart
                    </Button>
                    <Button onClick={handleClose} color="default">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* <div>
            <Link color="primary" href="#" onClick={preventDefault}>
                More info
            </Link>
        </div> */}
        </React.Fragment>
    );
}
